<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      $blockClass(blockClass, 'prices'),
      { 'product-prices--reverse': reverse },
    ]"
  >
    <span
      v-if="formattedCompareAtPrice && !reverse"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
      v-text="formattedCompareAtPrice"
    />

    <span
      class="product-prices__price"
      :class="[
        $blockClass(blockClass, 'price'),
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
      v-text="formattedPrice"
    />

    <span
      v-if="formattedCompareAtPrice && reverse"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
      v-text="formattedCompareAtPrice"
    />

    <span
      v-if="available === false"
      class="product-prices__sold-out"
      :class="$blockClass(blockClass, 'sold-out')"
      v-text="$string('product.prices.sold_out')"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} available - If the product is available.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number|Boolean} compareAtPrice - Compare at price if it exists.
 * @param {String} [element] - Custom element override.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [reverse] - Reverse order of prices.
 *
 */
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    compareAtPrice: {
      type: [Number, Boolean],
      default: false,
      required: true,
    },
    compareAtPriceHighest: {
      type: [Number, Boolean],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    price: {
      type: [Number, Boolean],
      default: 0,
      required: true,
    },
    maxPrice: {
      type: [Number, Boolean],
      default: 0,
      required: true,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      if (!this.compareAtPriceHighest || this.compareAtPrice === this.compareAtPriceHighest){
        return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice)
        : ''
      }

      return this.compareAtPrice && this.price < this.compareAtPrice
        ? `${this.$formatMoney(this.compareAtPrice)} - ${this.$formatMoney(this.compareAtPriceHighest)}`
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      if (this.price && this.maxPrice && this.price !== this.maxPrice){
        return `${this.$formatMoney(this.price)} - ${this.$formatMoney(this.maxPrice)}`
      }

      return this.price ? this.$formatMoney(this.price) : ''
    },
  },
}
</script>
